// ConfirmationModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface ConfirmationModalProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  trigger: React.ReactNode;
}

const ModalOverlay = styled.div<{ show: boolean }>`
  display: ${props => props.show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  min-width: 300px;
  position: relative;
`;

const Message = styled.p`
  margin: 0 0 20px 0;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #4CAF50;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #f44336;
  color: white;
`;

const TriggerContainer = styled.div`
  display: inline-block;
  position: relative;
`;

export const ConfirmeDialog: React.FC<ConfirmationModalProps> = ({
  message,
  onConfirm,
  onCancel,
  trigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm();
    setIsOpen(false);
  };

  const handleCancel = () => {
    onCancel();
    setIsOpen(false);
  };

  return (
    <TriggerContainer>
      <div onClick={() => setIsOpen(true)}>
        {trigger}
      </div>
      <ModalOverlay show={isOpen}>
        <ModalContent ref={modalRef}>
          <Message>{message}</Message>
          <ButtonContainer>
            <ConfirmButton onClick={handleConfirm}>Sim</ConfirmButton>
            <CancelButton onClick={handleCancel}>Não</CancelButton>
          </ButtonContainer>
        </ModalContent>
      </ModalOverlay>
    </TriggerContainer>
  );
};